import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FileText, Eye, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUOTE_STATUSES = ['pending', 'reviewed', 'quoted', 'converted', 'closed'];

export default function AdminQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/admin/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API}/admin/quotes/${quoteId}/status?status=${status}`);
      toast.success('Quote status updated');
      fetchQuotes();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadFile = (quote) => {
    if (!quote.file_data || !quote.file_name) return;
    
    const link = document.createElement('a');
    link.href = `data:application/octet-stream;base64,${quote.file_data}`;
    link.download = quote.file_name;
    link.click();
  };

  return (
    <AdminLayout title="Quote Requests">
      <div data-testid="admin-quotes">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-20 bg-white">
            <FileText size={48} className="mx-auto text-[#E5E7EB] mb-4" />
            <h3 className="font-semibold text-[#1A1A1A] mb-2">No quote requests yet</h3>
            <p className="text-[#6B7280]">Quote requests will appear here</p>
          </div>
        ) : (
          <div className="bg-white shadow-sm overflow-x-auto">
            <table className="w-full" data-testid="quotes-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F9F7F2]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Customer</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Style</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Budget</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote) => (
                  <tr key={quote.id} className="border-b border-[#E5E7EB]">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-[#1A1A1A]">{quote.name}</p>
                        <p className="text-xs text-[#6B7280]">{quote.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[#6B7280]">{quote.cabinet_style}</td>
                    <td className="py-3 px-4 text-[#6B7280]">{quote.budget_range}</td>
                    <td className="py-3 px-4 text-sm text-[#6B7280]">{formatDate(quote.created_at)}</td>
                    <td className="py-3 px-4">
                      <Select
                        value={quote.status}
                        onValueChange={(value) => updateStatus(quote.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {QUOTE_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setSelectedQuote(selectedQuote?.id === quote.id ? null : quote)}
                          data-testid={`view-quote-${quote.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                        {quote.file_data && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => downloadFile(quote)}
                            data-testid={`download-file-${quote.id}`}
                          >
                            <Download size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Quote Details */}
            {selectedQuote && (
              <div className="border-t border-[#E5E7EB] p-6 bg-[#F9F7F2]" data-testid="quote-details">
                <h3 className="font-semibold text-[#1A1A1A] mb-4">Quote Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-[#6B7280] mb-2">Contact Information</h4>
                    <p className="text-[#1A1A1A] font-medium">{selectedQuote.name}</p>
                    <p className="text-sm text-[#6B7280]">{selectedQuote.email}</p>
                    <p className="text-sm text-[#6B7280]">{selectedQuote.phone}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[#6B7280] mb-2">Kitchen Details</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-[#6B7280]">Dimensions:</span> {selectedQuote.kitchen_dimensions}</p>
                      <p><span className="text-[#6B7280]">Style:</span> {selectedQuote.cabinet_style}</p>
                      <p><span className="text-[#6B7280]">Budget:</span> {selectedQuote.budget_range}</p>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <h4 className="text-sm font-medium text-[#6B7280] mb-2">Message</h4>
                    <p className="text-[#1A1A1A] bg-white p-4 rounded">
                      {selectedQuote.message || 'No additional message'}
                    </p>
                  </div>
                  {selectedQuote.file_name && (
                    <div className="md:col-span-2">
                      <h4 className="text-sm font-medium text-[#6B7280] mb-2">Attached File</h4>
                      <Button variant="outline" onClick={() => downloadFile(selectedQuote)}>
                        <Download size={16} className="mr-2" />
                        {selectedQuote.file_name}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
